import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Award, Users, BookOpen, Star } from 'lucide-react';

const About = ({ isPreview = false }) => {
  const stats = [
    { value: '12', label: 'Years of Experience', icon: <Star size={22} /> },
    { value: '150+', label: 'Astro Consulting', icon: <Award size={22} /> },
    { value: '999+', label: 'Ceremonies Done', icon: <BookOpen size={22} /> },
    { value: '10+', label: 'Sanskrit Classes Given', icon: <Users size={22} /> },
  ];

  const infoItems = [
    { label: 'PLACE', value: '26 Naretha Court, Morayfield QLD 4506' },
    { label: 'MAIL', value: 'Prakashupadhyaya835@gmail.com', href: 'mailto:Prakashupadhyaya835@gmail.com' },
    { label: 'PHONE', value: '+61 416 656 862', href: 'tel:+61416656862' },
  ];

  const experiences = [
    {
      date: 'From 2018 to Present',
      source: '— Udbodha ra Jyotirvidhya',
      title: 'Vedic Pujari & Hindu Priest',
      desc: 'Performing authentic Vedic puja ceremonies including Hindu Weddings (Vivaha Sanskar), Griha Pravesh, Satyanarayan Puja, Rudrabhishek, Bratabandha, Shraddha, and all major life-cycle rituals for the Hindu community in Australia and beyond.',
    },
    {
      date: 'From 2019 to Present',
      source: '— Udbodha ra Jyotirvidhya',
      title: 'Vedic Astrologer (Jyotish)',
      desc: 'Providing in-depth Vedic astrology consultations using Janma Kundali (birth chart) analysis — covering horoscope reading, Kundali matching, Graha Dosh remedies, career guidance, and planetary transit interpretations rooted in classical Jyotish Shastra.',
    },
    {
      date: 'From 2020 to Present',
      source: '— Udbodha ra Jyotirvidhya',
      title: 'Nepali, Sanskrit & Hindu Education',
      desc: 'Teaching Nepali language (spoken, reading, writing) and Sanskrit — the sacred language of the Vedas — to students of all levels. Also conducting cultural and religious education sessions on Hindu Dharma, traditions, festivals, and philosophy for diaspora communities.',
    },
    {
      date: 'From 2021 to Present',
      source: '— Udbodha ra Jyotirvidhya',
      title: 'Spiritual & Dharmic Consultant',
      desc: 'Offering personal one-on-one spiritual guidance sessions to individuals and families seeking clarity in dharmic life choices, Hindu philosophy, sacred rituals, and holistic wellbeing — blending ancient Vedic wisdom with compassionate, practical counsel.',
    },
  ];

  const displayedExperiences = isPreview ? experiences.slice(0, 2) : experiences;

  return (
    <div className={`appt-page ${isPreview ? 'appt-page-preview' : ''}`}>

      {/* ── Hero (Hidden in Preview) ─────────────────────────────────── */}
      {!isPreview && (
        <div className="appt-hero">
          <div className="appt-hero-badge">
            <Sparkles size={16} />
            <span>Nice to meet you!</span>
          </div>
          <h1 className="appt-hero-title">
            Prakash <span>Upadhyaya</span>
          </h1>
          <p className="appt-hero-sub">
            Vedic Pujari &amp; Astrologer based in Morayfield, Queensland, Australia — dedicated to spiritual guidance and sacred rituals rooted in Hindu tradition.
          </p>
        </div>
      )}

      <div className="appt-container">

        {/* ── Stats Row ──────────────────────────────────────────────── */}
        <div className="about-stats-row appt-fade-in">
          {stats.map((s, i) => (
            <div key={i} className="appt-card about-stat-card">
              <div className="about-stat-icon">{s.icon}</div>
              <div className="about-stat-value">{s.value}</div>
              <div className="about-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Bio + Info ─────────────────────────────────────────────── */}
        <div className="appt-card about-bio-card appt-fade-in">
          <div className="appt-card-header">
            <Sparkles size={18} />
            <h3>About Me</h3>
          </div>
          <div className="about-bio-grid">
            {/* Left text */}
            <div className="about-bio-text">
              <p>
                Hi! I'm <strong style={{ color: '#6b5ce7' }}>Prakash Upadhyaya</strong>, a Vedic Pujari and Astrologer deeply rooted in the traditions of Hinduism, dedicated to spiritual guidance and sacred rituals.
              </p>
              <p>
                Based in Morayfield, Queensland, Australia, I offer Vedic puja ceremonies, astrological consultations, and spiritual counsel drawing from ancient Hindu wisdom. Feel free to reach out for guidance and blessings.
              </p>
              <Link
                to="/appointment"
                className="appt-btn-primary about-bio-cta"
                id="about-book-btn"
              >
                Book a Consultation
              </Link>
            </div>
            {/* Right info */}
            <div className="about-info-list">
              {infoItems.map((item, i) => (
                <div key={i} className="about-info-item">
                  <span className="about-info-label">{item.label}</span>
                  {item.href ? (
                    <a href={item.href} className="about-info-value about-info-link">{item.value}</a>
                  ) : (
                    <span className="about-info-value">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Experience ─────────────────────────────────────────────── */}
        <div className="appt-card appt-fade-in">
          <div className="appt-card-header">
            <Award size={18} />
            <h3>Experience</h3>
          </div>
          <div className="about-exp-grid">
            {displayedExperiences.map((exp, i) => (
              <div key={i} className="about-exp-card">
                <div className="about-exp-meta">
                  <span className="about-exp-date">{exp.date}</span>
                  <span className="about-exp-source">{exp.source}</span>
                </div>
                <h4 className="about-exp-title">{exp.title}</h4>
                <p className="about-exp-desc">{exp.desc}</p>
              </div>
            ))}
          </div>

          {/* See More Experience Button for MainPage Preview */}
          {isPreview && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
              <Link to="/about" className="appt-btn-primary" style={{ display: 'inline-flex', padding: '12px 28px', borderRadius: '12px', textDecoration: 'none' }}>
                See More Experience
              </Link>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default About;
